import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import type { AppRoute } from "../../shared/lib/routes";
import { useAuth } from "../../app/AuthContext";
import { navigate } from "../../app/routes";
import type {
  ImportMode,
  IngestionIssueRead,
  IngestionJobRead,
  IngestionUploadResponse,
  KnowledgeCardDetail,
  KnowledgeCardRemovalRead,
  KnowledgeCardSummary,
  KnowledgeSnapshotRead,
} from "../../shared/api/types";
import { collectPermissions } from "../../shared/lib/permissions";
import { formatDateTime, formatFileSize } from "../../shared/lib/format";
import { Badge, Button, Callout, Card, EmptyState, Field, IconButton, Input, Modal, Select, Spinner } from "../../shared/ui/Primitives";
import { Icon } from "../../shared/ui/Icon";
import { JsonView } from "../../shared/ui/JsonView";
import { useToast } from "../../shared/ui/Toast";
import { appConfig } from "../../app/config";
import { CardDetail } from "./CardDetail";
import { CardEditorModal } from "./CardEditorModal";

const KnowledgeGraphTab = lazy(async () => {
  const module = await import("./KnowledgeGraphTab");
  return { default: module.KnowledgeGraphTab };
});

const importModes: Array<{ value: ImportMode; label: string; description: string; accept: string; multiple: boolean }> = [
  { value: "full_zip", label: "Полный ZIP", description: "Полный снимок Obsidian. Отсутствующие карточки станут кандидатами на удаление.", accept: ".zip,application/zip", multiple: false },
  { value: "partial_zip", label: "Частичный ZIP", description: "Добавляет и обновляет карточки, не удаляя отсутствующие.", accept: ".zip,application/zip", multiple: false },
  { value: "single_markdown", label: "Один Markdown", description: "Для файла без ID приложение присвоит номер по выбранной папке.", accept: ".md,text/markdown,text/plain", multiple: false },
  { value: "multi_markdown", label: "Несколько Markdown", description: "Импорт набора отдельных карточек; ID уже должны быть внутри файлов.", accept: ".md,text/markdown,text/plain", multiple: true },
];

const targetFolders = ["00 Входящие", "10 Карточка", "20 Источники", "30 Карты"];

const statusLabels: Record<string, string> = {
  queued: "В очереди",
  running: "Обработка",
  awaiting_publication: "Ожидает публикации",
  completed: "Завершён",
  failed: "Ошибка",
  cancelled: "Отменён",
  draft: "Черновик",
  active: "Активна",
  archived: "Архив",
};

function statusTone(status: string): "neutral" | "success" | "warning" | "danger" | "info" {
  if (["active", "completed"].includes(status)) return "success";
  if (["failed", "cancelled"].includes(status)) return "danger";
  if (["running", "queued"].includes(status)) return "info";
  if (["awaiting_publication", "draft"].includes(status)) return "warning";
  return "neutral";
}

export function KnowledgeScreen({ route }: { route: Extract<AppRoute, { name: "knowledge" }> }) {
  const { api, user } = useAuth();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const activeTab = route.tab ?? "cards";
  return (
    <div className="section-page knowledge-page">
      <header className="section-header">
        <div><h1>База знаний</h1><p>Карточки Obsidian, проверки импорта и опубликованные версии.</p></div>
        <nav className="tabs" aria-label="Разделы базы знаний">
          <button className={activeTab === "cards" ? "is-active" : ""} onClick={() => navigate({ name: "knowledge", tab: "cards" })}>Карточки</button>
          <button className={activeTab === "graph" ? "is-active" : ""} onClick={() => navigate({ name: "knowledge", tab: "graph" })}>Граф</button>
          {permissions.has("knowledge.upload") ? <button className={activeTab === "imports" ? "is-active" : ""} onClick={() => navigate({ name: "knowledge", tab: "imports" })}>Загрузка</button> : null}
          <button className={activeTab === "snapshots" ? "is-active" : ""} onClick={() => navigate({ name: "knowledge", tab: "snapshots" })}>Версии</button>
        </nav>
      </header>
      {activeTab === "imports" && permissions.has("knowledge.upload") ? <ImportsTab /> : null}
      {activeTab === "snapshots" ? <SnapshotsTab canPublish={permissions.has("knowledge.publish")} /> : null}
      {activeTab === "cards" ? <CardsTab requestedCardId={route.cardId} /> : null}
      {activeTab === "graph" ? <Suspense fallback={<div className="centered"><Spinner /><span>Загружаем 3D-граф…</span></div>}><KnowledgeGraphTab /></Suspense> : null}
    </div>
  );
}

function CardsTab({ requestedCardId }: { requestedCardId?: string }) {
  const { api, user } = useAuth();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const { showToast } = useToast();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [cards, setCards] = useState<KnowledgeCardSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<KnowledgeCardDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<KnowledgeCardDetail | null>(null);
  const [removingCard, setRemovingCard] = useState<KnowledgeCardDetail | null>(null);
  const [removalResult, setRemovalResult] = useState<KnowledgeCardRemovalRead | null>(null);
  const [removing, setRemoving] = useState(false);
  const limit = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await api.listCards({ query: submittedQuery || undefined, limit, offset });
      setCards(page.items); setTotal(page.total);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось загрузить карточки", "danger");
      setCards([]); setTotal(0);
    } finally { setLoading(false); }
  }, [api, submittedQuery, offset, showToast]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!requestedCardId) { setDetail(null); return; }
    setDetailLoading(true);
    api.getCard(requestedCardId)
      .then(setDetail)
      .catch((error) => { showToast(error instanceof Error ? error.message : "Карточка не найдена", "danger"); navigate({ name: "knowledge", tab: "cards" }, true); })
      .finally(() => setDetailLoading(false));
  }, [api, requestedCardId, showToast]);

  function search(event: FormEvent) { event.preventDefault(); setOffset(0); setSubmittedQuery(query.trim()); }

  return (
    <div className="content-area">
      <form className="toolbar" onSubmit={search}>
        <label className="search-box search-box--wide"><Icon name="search" size={18} /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ID, название или часть названия" /></label>
        <Button type="submit" variant="secondary">Найти</Button>
        {permissions.has("knowledge.edit") ? <Button type="button" icon="plus" onClick={() => { setEditingCard(null); setEditorOpen(true); }}>Новая карточка</Button> : null}
        <span className="toolbar__count">Найдено: {total}</span>
      </form>
      {loading ? <div className="centered"><Spinner /><span>Загружаем карточки…</span></div> : null}
      {!loading && !cards.length ? <EmptyState title="Карточки не найдены" description="Проверьте запрос или наличие активной версии базы." icon="database" /> : null}
      <div className="card-table">
        {cards.map((card) => (
          <button key={card.version_id} type="button" className="card-table__row" onClick={() => navigate({ name: "knowledge", tab: "cards", cardId: card.external_id })}>
            <div><strong>{card.external_id}</strong><Badge tone={card.reviewed ? "success" : "warning"}>{card.reviewed ? "Проверена" : card.status}</Badge></div>
            <div><strong>{card.title}</strong><span>{card.source_path}</span></div>
            <div><span>{card.card_type}</span><span>{card.product_level || "—"}</span></div>
            <Icon name="chevron" size={18} />
          </button>
        ))}
      </div>
      {total > limit ? <div className="pagination"><Button variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Назад</Button><span>{offset + 1}–{Math.min(offset + limit, total)} из {total}</span><Button variant="secondary" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Далее</Button></div> : null}
      <Modal
        open={Boolean(requestedCardId)}
        title={detail?.title || requestedCardId || "Карточка"}
        onClose={() => navigate({ name: "knowledge", tab: "cards" })}
        wide
        footer={detail && permissions.has("knowledge.edit") ? (
          <>
            <Button variant="danger" onClick={() => { setRemovingCard(detail); setRemovalResult(null); navigate({ name: "knowledge", tab: "cards" }); }}>Убрать из базы</Button>
            <Button variant="secondary" onClick={() => navigate({ name: "knowledge", tab: "cards" })}>Закрыть</Button>
            <Button icon="edit" onClick={() => { setEditingCard(detail); setEditorOpen(true); navigate({ name: "knowledge", tab: "cards" }); }}>Редактировать</Button>
          </>
        ) : undefined}
      >
        {detailLoading ? <div className="centered"><Spinner /></div> : detail ? <CardDetail detail={detail} onOpenCard={(id) => navigate({ name: "knowledge", tab: "cards", cardId: id })} /> : null}
      </Modal>
      <CardEditorModal
        open={editorOpen}
        detail={editingCard}
        onClose={() => { setEditorOpen(false); setEditingCard(null); }}
        onSubmitted={() => void load()}
      />
      <Modal
        open={Boolean(removingCard)}
        title={removalResult ? `${removalResult.external_id} подготовлена к исключению` : `Убрать ${removingCard?.external_id ?? "карточку"} из базы?`}
        onClose={() => { setRemovingCard(null); setRemovalResult(null); }}
        footer={removalResult ? (
          <>
            <Button variant="secondary" onClick={() => { setRemovingCard(null); setRemovalResult(null); }}>Закрыть</Button>
            <Button onClick={() => { setRemovingCard(null); setRemovalResult(null); navigate({ name: "knowledge", tab: "snapshots" }); }}>Перейти к версиям</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setRemovingCard(null)}>Отмена</Button>
            <Button
              variant="danger"
              disabled={removing || !removingCard}
              onClick={async () => {
                if (!removingCard) return;
                setRemoving(true);
                try {
                  setRemovalResult(await api.createKnowledgeCardRemoval(removingCard.external_id, removingCard.version_id));
                  showToast("Кандидатный снимок без карточки создан", "success");
                } catch (error) {
                  showToast(error instanceof Error ? error.message : "Не удалось подготовить исключение карточки", "danger");
                } finally { setRemoving(false); }
              }}
            >{removing ? "Подготавливаем…" : "Создать версию без карточки"}</Button>
          </>
        )}
      >
        {removalResult ? (
          <div className="stack">
            <Callout tone="success" title="Создан кандидатный снимок">
              Активная база пока не изменилась. Проверьте и опубликуйте версию {removalResult.snapshot.version_number}.
            </Callout>
            <Callout tone="warning" title="Удалите источник вручную">
              После публикации удалите из Obsidian файл <code>{removalResult.source_path}</code>, иначе следующий полный импорт вернёт карточку.
            </Callout>
            {removalResult.incoming_reference_count ? <Callout tone="warning" title="Остались входящие связи">На карточку ссылаются другие активные карточки: {removalResult.incoming_reference_count}. Их нужно проверить и отредактировать отдельно.</Callout> : null}
          </div>
        ) : (
          <div className="stack">
            <Callout tone="warning" title="Это обратимая операция">
              Карточка исчезнет только после публикации нового снимка. Её версии и ссылки из истории бесед сохранятся.
            </Callout>
            <p>После публикации потребуется вручную удалить <code>{removingCard?.source_path}</code> из Obsidian.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function ImportsTab() {
  const { api } = useAuth();
  const { showToast } = useToast();
  const [mode, setMode] = useState<ImportMode>("full_zip");
  const [targetFolder, setTargetFolder] = useState("10 Карточка");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState<IngestionUploadResponse | null>(null);
  const [jobs, setJobs] = useState<IngestionJobRead[]>([]);
  const [selectedJob, setSelectedJob] = useState<IngestionJobRead | null>(null);
  const [issues, setIssues] = useState<IngestionIssueRead[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const modeConfig = importModes.find((item) => item.value === mode) ?? importModes[0]!;
  const loadJobs = useCallback(async () => {
    try {
      const page = await api.listImports(50);
      setJobs(page.items);
      setSelectedJob((current) => current ? page.items.find((item) => item.id === current.id) ?? current : page.items[0] ?? null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось загрузить историю импорта", "danger");
    } finally { setLoadingJobs(false); }
  }, [api, showToast]);
  useEffect(() => { void loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!selectedJob) { setIssues([]); return; }
    void api.getImportIssues(selectedJob.id).then(setIssues).catch(() => setIssues([]));
    if (!["queued", "running"].includes(selectedJob.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const updated = await api.getImport(selectedJob.id);
        setSelectedJob(updated);
        setJobs((current) => current.map((item) => item.id === updated.id ? updated : item));
        if (!["queued", "running"].includes(updated.status)) {
          window.clearInterval(timer);
          setIssues(await api.getImportIssues(updated.id));
        }
      } catch { /* next manual refresh will retry */ }
    }, appConfig.jobPollIntervalMs);
    return () => window.clearInterval(timer);
  }, [api, selectedJob?.id, selectedJob?.status]);

  function selectFiles(event: ChangeEvent<HTMLInputElement>) { setFiles(Array.from(event.target.files ?? [])); }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!files.length) return;
    setUploading(true);
    try {
      const response = await api.uploadKnowledge({ mode, files, targetFolder: mode === "single_markdown" ? targetFolder : undefined });
      setLastUpload(response); setSelectedJob(response.job); setFiles([]);
      await loadJobs();
      showToast("Файлы приняты и поставлены в очередь", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось загрузить файлы", "danger");
    } finally { setUploading(false); }
  }

  async function downloadUpdatedFile(fileId: string) {
    try {
      const result = await api.downloadImportFile(fileId);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a"); anchor.href = url; anchor.download = result.filename || "updated-card.md"; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) { showToast(error instanceof Error ? error.message : "Не удалось скачать файл", "danger"); }
  }

  return (
    <div className="content-area knowledge-import-layout">
      <Card className="upload-card">
        <h2>Загрузить знания</h2>
        <form className="stack" onSubmit={submit}>
          <Field label="Режим импорта" hint={modeConfig.description}>
            <Select value={mode} onChange={(event) => { setMode(event.target.value as ImportMode); setFiles([]); }}>
              {importModes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </Select>
          </Field>
          {mode === "single_markdown" ? <Field label="Папка в Obsidian" hint="По папке выбирается реестр KB, SRC, MAP или INB для файла без ID."><Select value={targetFolder} onChange={(event) => setTargetFolder(event.target.value)}>{targetFolders.map((folder) => <option key={folder}>{folder}</option>)}</Select></Field> : null}
          <label className="file-drop"><Icon name="upload" size={28} /><strong>{files.length ? `Выбрано файлов: ${files.length}` : "Выберите файлы"}</strong><span>{modeConfig.accept.includes("zip") ? "ZIP-архив" : "Markdown .md"}</span><input type="file" accept={modeConfig.accept} multiple={modeConfig.multiple} onChange={selectFiles} /></label>
          {files.length ? <div className="selected-files">{files.map((file) => <span key={`${file.name}-${file.lastModified}`}><Icon name="paperclip" size={15} />{file.name}<small>{formatFileSize(file.size)}</small></span>)}</div> : null}
          <Button type="submit" icon="upload" disabled={!files.length || uploading}>{uploading ? "Загружаем…" : "Начать проверку"}</Button>
        </form>
        {lastUpload && Object.keys(lastUpload.assigned_external_ids).length ? <Callout tone="success" title="Назначены идентификаторы"><JsonView value={lastUpload.assigned_external_ids} /></Callout> : null}
        {lastUpload?.updated_file_ids.length ? <div className="download-list"><strong>Верните обновлённые файлы в Obsidian</strong>{lastUpload.updated_file_ids.map((id) => <Button key={id} size="small" variant="secondary" icon="download" onClick={() => void downloadUpdatedFile(id)}>Скачать Markdown</Button>)}</div> : null}
      </Card>

      <Card className="jobs-card">
        <header className="card-header"><div><h2>История загрузок</h2><span>{jobs.length}</span></div><IconButton icon="refresh" label="Обновить" onClick={() => void loadJobs()} /></header>
        {loadingJobs ? <div className="centered"><Spinner /></div> : null}
        <div className="job-list">{jobs.map((job) => <button key={job.id} type="button" className={selectedJob?.id === job.id ? "is-active" : ""} onClick={() => setSelectedJob(job)}><div><strong>{importModes.find((item) => item.value === job.mode)?.label}</strong><Badge tone={statusTone(job.status)}>{statusLabels[job.status] || job.status}</Badge></div><span>{formatDateTime(job.created_at)}</span></button>)}</div>
      </Card>

      <Card className="job-detail-card">
        {selectedJob ? <JobDetail job={selectedJob} issues={issues} onPublish={async () => { if (!selectedJob.candidate_snapshot_id) return; await api.publishSnapshot(selectedJob.candidate_snapshot_id); showToast("Версия базы опубликована", "success"); await loadJobs(); }} /> : <EmptyState title="Выберите загрузку" description="Здесь появятся результаты проверки." icon="upload" />}
      </Card>
    </div>
  );
}

function JobDetail({ job, issues, onPublish }: { job: IngestionJobRead; issues: IngestionIssueRead[]; onPublish: () => Promise<void> }) {
  const { user } = useAuth();
  const permissions = collectPermissions(user);
  const [publishing, setPublishing] = useState(false);
  const grouped = { error: issues.filter((item) => item.severity === "error"), warning: issues.filter((item) => item.severity === "warning"), info: issues.filter((item) => item.severity === "info") };
  return (
    <div className="job-detail">
      <header><div><h2>Результат проверки</h2><code>{job.id}</code></div><Badge tone={statusTone(job.status)}>{statusLabels[job.status] || job.status}</Badge></header>
      {job.error_message ? <Callout tone="danger">{job.error_message}</Callout> : null}
      {["queued", "running"].includes(job.status) ? <div className="centered"><Spinner /><span>Worker обрабатывает файлы…</span></div> : null}
      <section><h3>Сводка</h3><JsonView value={job.summary} /></section>
      <section><h3>Сообщения проверки</h3><div className="issue-summary"><Badge tone="danger">Ошибки: {grouped.error.length}</Badge><Badge tone="warning">Предупреждения: {grouped.warning.length}</Badge><Badge tone="info">Информация: {grouped.info.length}</Badge></div>{issues.length ? <div className="issue-list">{issues.map((issue) => <article key={issue.id} className={`issue issue--${issue.severity}`}><Icon name={issue.severity === "error" ? "error" : issue.severity === "warning" ? "warning" : "info"} size={18} /><div><strong>{issue.message}</strong><span>{[issue.external_id, issue.source_path, issue.line_number ? `строка ${issue.line_number}` : null].filter(Boolean).join(" · ")}</span><code>{issue.code}</code></div></article>)}</div> : <p className="muted">Ошибок и предупреждений нет.</p>}</section>
      {job.status === "awaiting_publication" && job.candidate_snapshot_id ? <div className="publish-box"><div><strong>Кандидат готов к публикации</strong><span>Снимок {job.candidate_snapshot_id}</span></div><Button disabled={!permissions.has("knowledge.publish") || publishing || grouped.error.length > 0} onClick={async () => { setPublishing(true); try { await onPublish(); } finally { setPublishing(false); } }}>{publishing ? "Публикуем…" : "Опубликовать"}</Button></div> : null}
    </div>
  );
}

function SnapshotsTab({ canPublish }: { canPublish: boolean }) {
  const { api } = useAuth();
  const { showToast } = useToast();
  const [snapshots, setSnapshots] = useState<KnowledgeSnapshotRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState<string | null>(null);
  const load = useCallback(async () => { setLoading(true); try { setSnapshots(await api.listSnapshots()); } catch (error) { showToast(error instanceof Error ? error.message : "Не удалось загрузить версии", "danger"); } finally { setLoading(false); } }, [api, showToast]);
  useEffect(() => { void load(); }, [load]);
  async function publish(snapshot: KnowledgeSnapshotRead) { setPublishing(snapshot.id); try { await api.publishSnapshot(snapshot.id); showToast(`Версия ${snapshot.version_number} активирована`, "success"); await load(); } catch (error) { showToast(error instanceof Error ? error.message : "Не удалось опубликовать версию", "danger"); } finally { setPublishing(null); } }
  return (
    <div className="content-area">
      <Callout tone="info">Каждый ответ сохраняет идентификатор использованного снимка базы. Повторная активация прежнего снимка выполняет откат без удаления новых данных.</Callout>
      {loading ? <div className="centered"><Spinner /></div> : null}
      <div className="snapshot-grid">{snapshots.map((snapshot) => <Card key={snapshot.id} className={snapshot.status === "active" ? "snapshot-card is-active" : "snapshot-card"}><header><div><strong>Версия {snapshot.version_number}</strong><span>{snapshot.name}</span></div><Badge tone={statusTone(snapshot.status)}>{statusLabels[snapshot.status] || snapshot.status}</Badge></header><JsonView value={snapshot.summary} /><footer><span>{formatDateTime(snapshot.created_at)}</span>{canPublish && snapshot.status !== "active" ? <Button size="small" variant="secondary" disabled={publishing === snapshot.id} onClick={() => void publish(snapshot)}>{publishing === snapshot.id ? "Активируем…" : "Сделать активной"}</Button> : null}</footer></Card>)}</div>
      {!loading && !snapshots.length ? <EmptyState title="Версий пока нет" description="Загрузите и опубликуйте первую базу знаний." icon="database" /> : null}
    </div>
  );
}
